import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent, ClipboardEvent } from 'react';
import { useChatStore } from '../../store/chatStore';
import { useChat } from '../../hooks/useChat';
import { Send, Square, Paperclip, X, Image as ImageIcon, Clipboard, User, Headphones, Code, FileText } from 'lucide-react';
import type { Persona } from '../../types';

const personaOptions: { key: Persona; icon: typeof User; label: string }[] = [
  { key: 'client', icon: User, label: 'Client' },
  { key: 'support', icon: Headphones, label: 'Support' },
  { key: 'developer', icon: Code, label: 'Developer' },
  { key: 'rfp', icon: FileText, label: 'RFP Response' },
];

interface AttachedFile {
  file: File;
  preview?: string;
  type: 'image' | 'document';
}

/**
 * Primary chat input component with support for file attachments, drag-and-drop,
 * clipboard image pasting, persona selection, and auto-resizing textarea.
 */
export function MessageInput() {
  const [input, setInput] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pasteHint, setPasteHint] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isLoading, suggestedInput, setSuggestedInput, persona, setPersona, rfpSubsection, setRfpSubsection } = useChatStore();
  const { sendMessage, abortRequest } = useChat();

  // Handle suggested input from WelcomeScreen topic clicks
  useEffect(() => {
    if (suggestedInput) {
      setInput(suggestedInput);
      setSuggestedInput(''); // Clear after populating
      // Focus the textarea
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [suggestedInput, setSuggestedInput]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  // Cleanup file previews on unmount
  useEffect(() => {
    return () => {
      attachedFiles.forEach(f => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
    };
  }, [attachedFiles]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const newFiles: AttachedFile[] = [];
    Array.from(files).forEach(file => {
      const isImage = file.type.startsWith('image/');
      const attachedFile: AttachedFile = {
        file,
        type: isImage ? 'image' : 'document',
        preview: isImage ? URL.createObjectURL(file) : undefined,
      };
      newFiles.push(attachedFile);
    });

    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => {
      const removed = prev[index];
      if (removed.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleSubmit = async () => {
    // Read isLoading directly from the store (not the render snapshot) to
    // prevent double-submit when two events fire before React re-renders.
    if ((!input.trim() && attachedFiles.length === 0) || useChatStore.getState().isLoading) return;

    // Images are sent to the backend (text extracted server-side).
    // Documents are not supported yet — say so VISIBLY, never drop silently.
    const imageFiles = attachedFiles.filter(f => f.type === 'image').slice(0, 3);
    const docFiles = attachedFiles.filter(f => f.type !== 'image');
    if (docFiles.length > 0) {
      window.alert(
        `Only images are supported in chat for now — these file(s) will not be sent:\n` +
        docFiles.map(f => f.file.name).join('\n')
      );
    }
    const oversized = imageFiles.filter(f => f.file.size > 4 * 1024 * 1024);
    if (oversized.length > 0) {
      window.alert(
        `Image(s) over the 4 MB limit will not be sent:\n` +
        oversized.map(f => f.file.name).join('\n')
      );
    }

    const sendable = imageFiles.filter(f => f.file.size <= 4 * 1024 * 1024);
    const attachments = await Promise.all(
      sendable.map(async f => ({
        filename: f.file.name || 'pasted-image.png',
        mime_type: f.file.type,
        data: await fileToBase64(f.file),
      }))
    );

    if (!input.trim() && attachments.length === 0) return; // nothing sendable left

    sendMessage(input.trim(), attachments.length > 0 ? attachments : undefined);
    setInput('');
    // Revoke object URLs to prevent memory leaks before clearing.
    attachedFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setAttachedFiles([]);
  };

  /** File → raw base64 (data: URI prefix stripped). */
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.substring(result.indexOf(',') + 1));
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Handle paste from clipboard (including screenshots)
  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardItems = e.clipboardData?.items;
    if (!clipboardItems) return;

    const imageItems: DataTransferItem[] = [];
    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];
      if (item.type.startsWith('image/')) {
        imageItems.push(item);
      }
    }

    if (imageItems.length > 0) {
      e.preventDefault(); // Prevent pasting image as text

      imageItems.forEach((item, index) => {
        const file = item.getAsFile();
        if (file) {
          // Create a friendly filename for pasted images
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const extension = file.type.split('/')[1] || 'png';
          const renamedFile = new File(
            [file],
            `pasted-image-${timestamp}-${index + 1}.${extension}`,
            { type: file.type }
          );

          const attachedFile: AttachedFile = {
            file: renamedFile,
            type: 'image',
            preview: URL.createObjectURL(file),
          };

          setAttachedFiles(prev => [...prev, attachedFile]);
        }
      });

      // Show brief paste confirmation
      setPasteHint(true);
      setTimeout(() => setPasteHint(false), 2000);
    }
  };

  const canSubmit = input.trim() || attachedFiles.length > 0;

  return (
    <div className="border-t border-gray-100 bg-white p-2 flex-shrink-0">
      <div className="max-w-3xl mx-auto">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt,.md"
          onChange={handleFileInputChange}
          className="hidden"
        />

        {/* Attached files preview */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
            {attachedFiles.map((file, index) => (
              <div
                key={index}
                className="relative group flex items-center gap-2 bg-white rounded-lg p-2 border border-gray-200"
              >
                {file.type === 'image' && file.preview ? (
                  <img
                    src={file.preview}
                    alt={file.file.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                    <Paperclip size={20} className="text-gray-400" />
                  </div>
                )}
                <span className="text-sm text-gray-600 max-w-[100px] truncate">
                  {file.file.name}
                </span>
                <button
                  onClick={() => removeFile(index)}
                  className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Persona selector pills */}
        <div className="flex items-center gap-0.5 mb-1">
          {personaOptions.map(({ key, icon: Icon, label }) => {
            const isActive = persona === key;
            return (
              <button
                key={key}
                onClick={() => setPersona(key)}
                className={`flex items-center gap-0.5 px-2 py-0.5 rounded-full transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-700 border border-primary-200'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 border border-transparent'
                }`}
                style={{ fontSize: '11px' }}
                aria-pressed={isActive}
              >
                <Icon size={10} />
                {label}
              </button>
            );
          })}

        </div>

        {/* RFP subsection input — visible only when RFP persona is active */}
        {persona === 'rfp' && (
          <div className="mb-1">
            <input
              type="text"
              value={rfpSubsection}
              onChange={(e) => setRfpSubsection(e.target.value)}
              placeholder="RFP Subsection (e.g. 5.2.1 Sales, pricing & quotations)"
              className="w-full px-3 py-1.5 text-xs bg-amber-50 border border-amber-200 rounded-md text-gray-700 placeholder-gray-400 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200 transition-all"
            />
          </div>
        )}

        {/* Main input container */}
        <div
          className={`relative flex items-end gap-2 bg-gray-50 rounded-lg p-2 border transition-all ${
            isDragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-100'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Attach file button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            aria-label="Attach file"
            title="Attach images or documents"
          >
            <Paperclip size={20} />
          </button>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={persona === 'rfp' ? "Enter the RFP question..." : "Ask a question about your documentation..."}
            className="flex-1 bg-transparent resize-none outline-none text-gray-800 placeholder-gray-400 min-h-[44px] max-h-[200px] py-2 text-sm font-sans leading-relaxed"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
            rows={1}
            disabled={isLoading}
            aria-label="Message input"
          />

          {/* Submit/Stop button */}
          {isLoading ? (
            <button
              onClick={abortRequest}
              className="flex-shrink-0 p-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
              aria-label="Stop generating"
            >
              <Square size={18} fill="currentColor" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`flex-shrink-0 p-2 rounded-lg transition-all ${
                canSubmit
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              aria-label="Send message"
            >
              <Send size={18} />
            </button>
          )}

          {/* Drag overlay */}
          {isDragOver && (
            <div className="absolute inset-0 bg-primary-50/90 rounded-lg flex items-center justify-center pointer-events-none">
              <div className="flex flex-col items-center gap-2 text-primary-600">
                <ImageIcon size={32} />
                <span className="font-medium">Drop files here</span>
              </div>
            </div>
          )}
        </div>

        {/* Helper text */}
        <div className="mt-2 flex items-center justify-center gap-4 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">Enter</kbd>
            <span>to send</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">Shift+Enter</kbd>
            <span>for new line</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">Ctrl+V</kbd>
            <span>paste image</span>
          </span>
          {pasteHint && (
            <span className="text-green-500 animate-pulse flex items-center gap-1">
              <Clipboard size={12} />
              Image pasted!
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
